import { Injectable, inject } from '@angular/core';
import { ENVIRONMENT } from 'shared';
import {
  CONSENT_APP_CONFIG, ConsentChange, ConsentRecord, ConsentService, ConsentState, SESSION_COOKIE,
  SESSION_IDLE_SECONDS, consentStateOf, readCookie, writeCookie, isUuidV4
} from './consent.service';

/**
 * WolfPack Tracking Engine — the app side of the landing SDK
 * (libs/analytics/src/landing/wp-track.js). Binding contracts:
 * docs/tracking/EVENT_CONTRACT_V1.md and, for consent, EVENT_CONTRACT_V2.md.
 *
 * - V2 consent gating (ConsentService is the only source of the state): with
 *   `pre_consent_mode: strict` nothing but `consent_change` is sent while the state
 *   is `none` or `necessary`; `cookieless` keeps the V1 behaviour for those states.
 *   An event is admitted against the state at the moment it is tracked — an event
 *   recorded before a later "Accept all" is never sent retroactively.
 * - Session id: with analytics the `wp_sid` cookie (`<uuid>.<last activity>`,
 *   refreshed on every batch, a new id after 30 minutes idle); otherwise — and as
 *   the first value of a fresh `wp_sid` — `?wps=` from the URL the app was opened
 *   with (put on the checkout link by the landing), else a fresh UUID in memory.
 * - `visitor_id` (from `wp_vid`) only with analytics, `click_ids` (gclid, gbraid,
 *   wbraid, fbclid, ttclid of the initial URL, memory only) only with marketing.
 *   Unlike the landing SDK, `wps` is deliberately NOT removed from the app URL: a
 *   history.replaceState behind the Router's back desyncs its URL tree, and a Router
 *   navigation would re-run guards on the checkout. The checkout replaces its URL on
 *   the next step anyway.
 * - Events go to `POST /api/t/e` with plain `fetch`, never HttpClient: the app's
 *   interceptors would turn a 401/503 of the tracking endpoint into a redirect to
 *   /login or the maintenance screen. Every error is swallowed.
 * - Nothing is sent until a caller tracks something, so apps that never use the
 *   service are unaffected.
 * - Matomo is a separate system and is not touched here.
 */

export type TrackingErrorCategory = 'validation' | 'captcha' | 'exists' | 'payment_declined' | 'network' | 'other';
export type TrackingCheckoutStep = 'account' | 'account_exists' | 'plan' | 'payment_method' | 'payment_confirmation' | 'success';
export type TrackingCheckoutStatus = 'view' | 'submit' | 'error';
export type TrackingFormStatus = 'start' | 'submit' | 'error' | 'complete';
export type TrackingForm = 'checkout' | 'contact';

/** `checkout_started.plan` allowlist (contract §3). */
export const TRACKING_PLANS = ['weekly', 'yearly', 'practitioner_weekly', 'practitioner_yearly',
                               'dna_health_special', 'platinum'] as const;
export type TrackingPlan = typeof TRACKING_PLANS[number];

interface TrackingEvent {
  /** Consent record at the time the event was tracked (undefined = brand config not yet known). */
  admitted?: boolean;
  record?: ConsentRecord | null;
  event_id: string;
  event_type: string;
  occurred_at: string;
  view_id: string;
  params: Record<string, unknown>;
}

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'] as const;
const CLICK_ID_VALUE = /^[A-Za-z0-9._-]{1,128}$/;
/** Allowed while no optional consent exists (category "necessary", contract §4). */
const ALWAYS_ALLOWED = new Set(['consent_change']);
const SEND_AT = 20;
const SEND_MS = 5000;
const MAX_BATCH = 50;
const RETRY_MS = [2000, 8000];

/** The query string the app was loaded with — before any router redirect drops it. */
const INITIAL_SEARCH = typeof window !== 'undefined' ? window.location.search : '';

function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  const b = new Uint8Array(16);
  try { crypto.getRandomValues(b); } catch { for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256); }
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function clickIdsFrom(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    const q = new URLSearchParams(search);
    CLICK_ID_KEYS.forEach(k => { const v = q.get(k); if (v && CLICK_ID_VALUE.test(v)) out[k] = v; });
  } catch { /* ignore */ }
  return out;
}

function wpsFrom(search: string): string | null {
  try {
    const v = (new URLSearchParams(search).get('wps') || '').toLowerCase();
    return UUID_V4.test(v) ? v : null;
  } catch { return null; }
}

/**
 * Maps an HTTP error of a funnel/checkout call onto the contract's error categories.
 * Status 0 = the request never got an answer.
 */
export function trackingErrorCategory(err: any): TrackingErrorCategory {
  const status: number | undefined = err?.status;
  const code = String(err?.error?.code ?? err?.error?.error ?? '');
  if (status === 0) return 'network';
  if (code === 'exists' || code === 'existing_account' || code === 'email_taken') return 'exists';
  if (/captcha/i.test(code)) return 'captcha';
  if (/declin/i.test(code)) return 'payment_declined';
  if (status === 422 || status === 400) return 'validation';
  return 'other';
}

@Injectable({ providedIn: 'root' })
export class TrackingService {
  private environment: any = inject(ENVIRONMENT, { optional: true });
  private consent = inject(ConsentService);
  private appPageId: string | null = inject(CONSENT_APP_CONFIG, { optional: true })?.pageId ?? null;

  private memorySessionId: string;
  private readonly viewId = uuid();
  private readonly endpoint: string;
  private readonly internal: boolean;
  private readonly test: boolean;
  private readonly webdriver: boolean;
  /** Click ids of the URL the app was loaded with — memory only, sent only with marketing. */
  private clickIds: Record<string, string>;

  private queues = new Map<string, TrackingEvent[]>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private checkoutStartedSent = false;
  /** Page id of the last tracked event (checkout pages); consent_change goes there too. */
  private lastPageId: string | null = null;

  constructor() {
    const hasWindow = typeof window !== 'undefined';
    this.memorySessionId = (hasWindow ? wpsFrom(window.location.search) : null) ?? wpsFrom(INITIAL_SEARCH) ?? uuid();
    this.clickIds = { ...clickIdsFrom(INITIAL_SEARCH), ...(hasWindow ? clickIdsFrom(window.location.search) : {}) };
    const base = String(this.environment?.serverUrl ?? '/api').replace(/\/+$/, '');
    this.endpoint = `${base}/t/e`;

    let internal = false;
    try { internal = localStorage.getItem('wpt.internal') === '1'; } catch { internal = false; }
    this.internal = internal;
    let test = false;
    try {
      test = new URLSearchParams(INITIAL_SEARCH).get('wp_test') === '1' ||
             (hasWindow && new URLSearchParams(window.location.search).get('wp_test') === '1');
    } catch { test = false; }
    this.test = test;
    let webdriver = false;
    try { webdriver = typeof navigator !== 'undefined' && (navigator as any).webdriver === true; } catch { webdriver = false; }
    this.webdriver = webdriver;

    this.consent.changes$.subscribe(change => this.onConsentChange(change));

    if (hasWindow) {
      try {
        window.addEventListener('pagehide', () => this.flush(true));
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') this.flush(true);
        });
      } catch { /* ignore */ }
    }
  }

  /**
   * Tracking session. Pass it as `tracking_session_id`: with analytics it is the
   * uuid part of `wp_sid`, otherwise the `?wps=` id (or the in-memory one).
   */
  get sessionId(): string {
    return this.currentSessionId(this.consent.state(), false);
  }

  /** Generic event; `params` must only hold keys the contract allows for `type`. */
  track(pageId: string, type: string, params: Record<string, string | number | boolean | string[] | null | undefined> = {}): void {
    try {
      if (!/^[a-z0-9-]{1,48}$/.test(pageId) || !/^[a-z_]{1,40}$/.test(type)) return;
      const always = ALWAYS_ALLOWED.has(type);
      // Admission against the consent state of THIS moment; unknown while the brand
      // config is still loading — then decided against the record captured now.
      const known = this.consent.config() !== null;
      if (known && !always && !this.allowed(this.consent.state())) return;
      const clean: Record<string, any> = {};
      Object.keys(params).forEach(k => { const v = params[k]; if (v !== null && v !== undefined) clean[k] = v; });
      if (type !== 'consent_change') this.lastPageId = pageId;
      const queue = this.queues.get(pageId) ?? [];
      queue.push({ event_id: uuid(), event_type: type, occurred_at: new Date().toISOString(),
                   view_id: this.viewId, params: clean,
                   admitted: always || known ? true : undefined,
                   record: known || always ? undefined : this.consent.record() });
      this.queues.set(pageId, queue);
      this.consent.ready();
      if (queue.length >= SEND_AT) this.flush(false);
      else if (!this.timer) this.timer = setTimeout(() => { this.timer = null; this.flush(false); }, SEND_MS);
    } catch { /* tracking must never break the checkout */ }
  }

  /**
   * Once per session (contract: dedup per session_id). An unknown plan is not sent —
   * by default the whole event is dropped; with `allowNoPlan` (a checkout that can
   * sell products outside the plan allowlist, e.g. the shared order wizard) it is
   * sent without `plan`, which the schema permits.
   */
  checkoutStarted(pageId: string, plan: string | null | undefined, sourcePageId: string,
                  options: { allowNoPlan?: boolean } = {}): void {
    if (this.checkoutStartedSent) return;
    const known = !!plan && (TRACKING_PLANS as readonly string[]).includes(plan);
    if (!known && !options.allowNoPlan) return;
    this.checkoutStartedSent = true;
    this.track(pageId, 'checkout_started', { plan: known ? plan : null, source_page_id: sourcePageId });
  }

  /** Has this app run already sent `checkout_started`? */
  get checkoutStartedAlready(): boolean { return this.checkoutStartedSent; }

  checkoutStep(pageId: string, step: TrackingCheckoutStep, status: TrackingCheckoutStatus,
               errorCategory?: TrackingErrorCategory | null): void {
    this.track(pageId, 'checkout_step', { step, status, error_category: status === 'error' ? (errorCategory ?? 'other') : null });
  }

  formInteraction(pageId: string, form: TrackingForm, status: TrackingFormStatus,
                  errorCategory?: TrackingErrorCategory | null): void {
    this.track(pageId, 'form_interaction', { form, status, error_category: status === 'error' ? (errorCategory ?? 'other') : null });
  }

  /** Sends everything queued; `beacon` on page exit (synchronous, uses what is known now). */
  flush(beacon = false): void {
    try {
      if (this.timer) { clearTimeout(this.timer); this.timer = null; }
      if (!beacon && this.consent.config() === null) {
        // Wait for the brand config (pre_consent_mode, policy_version) — never rejects.
        this.consent.ready().then(() => this.flush(false));
        return;
      }
      this.send(beacon, this.consent.state());
    } catch { /* swallow */ }
  }

  private send(beacon: boolean, state: ConsentState, sessionOverride?: string): void {
    const pv = this.consent.policyVersion;
    this.queues.forEach((queue, pageId) => {
      const events = queue.filter(e => {
        if (e.admitted) return true;
        // Tracked before the brand config was known: judge by the record of that moment.
        const r = e.record ?? null;
        const s: ConsentState = r && r.v === 1 && r.pv === pv ? consentStateOf(r.c) : 'none';
        return this.allowed(s);
      });
      while (events.length) {
        const payload = this.body(pageId, events.splice(0, MAX_BATCH), state, sessionOverride);
        let sent = false;
        if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
          try { sent = navigator.sendBeacon(this.endpoint, new Blob([payload], { type: 'application/json' })); } catch { sent = false; }
        }
        if (!sent) this.post(payload, 0);
      }
    });
    this.queues.clear();
  }

  /** May an optional event be sent in this state? */
  private allowed(state: ConsentState): boolean {
    if (state === 'analytics' || state === 'marketing') return true;
    return this.consent.preConsentMode === 'cookieless';
  }

  /**
   * A decision was stored (banner or preference center). Events queued before it
   * go out under the OLD state; then `consent_change` is sent in the new one. A
   * withdrawal also forgets the click ids and starts a new in-memory session, so
   * nothing after it can be linked to the visitor.
   */
  private onConsentChange(change: ConsentChange): void {
    try {
      if (this.timer) { clearTimeout(this.timer); this.timer = null; }
      const oldSession = this.currentSessionId(change.previousState, false);
      this.send(false, change.previousState, oldSession);
      const params = {
        consent_id: change.record.id,
        categories: [...change.record.c],
        policy_version: change.policyVersion,
        action: change.action,
        surface: change.surface
      };
      const lostAnalytics = (change.previousState === 'analytics' || change.previousState === 'marketing') &&
                            change.state !== 'analytics' && change.state !== 'marketing';
      if (lostAnalytics) {
        // The final consent_change still names the session it ends (the backend
        // unlinks visitor and click ids via the consent id), then we start over.
        const pageId = this.lastPageId ?? this.appPageId;
        if (pageId) {
          const ev: TrackingEvent = { event_id: uuid(), event_type: 'consent_change', occurred_at: new Date().toISOString(),
                                      view_id: this.viewId, params, admitted: true };
          this.post(this.body(pageId, [ev], change.state, oldSession), 0);
        }
        this.clickIds = {};
        this.memorySessionId = uuid();
        return;
      }
      if (change.state !== 'marketing') this.clickIds = {};
      if (change.previousState !== 'analytics' && change.previousState !== 'marketing') {
        // Continue the visible session into wp_sid rather than starting a new one.
        if (change.state === 'analytics' || change.state === 'marketing') this.memorySessionId = oldSession;
      }
      const pageId = this.lastPageId ?? this.appPageId;
      if (pageId) {
        this.track(pageId, 'consent_change', params);
        this.flush(false);
      }
    } catch { /* swallow */ }
  }

  /**
   * With analytics: `wp_sid` if present and active (else a fresh id after 30 min
   * idle; the in-memory/wps id when no cookie exists yet). `touch` writes the
   * cookie back with the current time (each batch = activity).
   */
  private currentSessionId(state: ConsentState, touch: boolean): string {
    if (state !== 'analytics' && state !== 'marketing') return this.memorySessionId;
    const now = Math.floor(Date.now() / 1000);
    let id = this.memorySessionId;
    const raw = readCookie(SESSION_COOKIE);
    if (raw) {
      const [sid, ts] = raw.split('.');
      const last = Number(ts);
      if (isUuidV4(sid) && Number.isFinite(last) && now - last <= SESSION_IDLE_SECONDS) id = sid;
      else id = uuid();
    }
    this.memorySessionId = id;
    // Only while analytics is granted NOW — a flush under the previous state right
    // after a withdrawal must not write the cookie back.
    if ((touch || !raw) && this.consent.analyticsGranted()) writeCookie(SESSION_COOKIE, `${id}.${now}`, null);
    return id;
  }

  private body(pageId: string, events: TrackingEvent[], state: ConsentState, sessionOverride?: string): string {
    const analytics = state === 'analytics' || state === 'marketing';
    const sessionId = sessionOverride ?? this.currentSessionId(state, true);
    const record = this.consent.validRecord();
    const body: Record<string, unknown> = {
      schema_version: 2, page_id: pageId, variant: 'a', session_id: sessionId,
      consent_state: state,
      policy_version: this.consent.policyVersion,
      events: events.map(({ admitted: _a, record: _r, ...e }) => e)
    };
    if (record && state !== 'none') body['consent_id'] = record.id;
    if (analytics) {
      const vid = this.consent.visitorId();
      if (vid) body['visitor_id'] = vid;
    }
    if (state === 'marketing' && Object.keys(this.clickIds).length) body['click_ids'] = { ...this.clickIds };
    if (this.internal) body['internal'] = true;
    if (this.test) body['test'] = true;
    if (this.webdriver) body['webdriver'] = true;
    return JSON.stringify(body);
  }

  private post(payload: string, attempt: number): void {
    if (typeof fetch === 'undefined') return;
    const retry = () => {
      if (attempt < RETRY_MS.length) setTimeout(() => this.post(payload, attempt + 1), RETRY_MS[attempt]);
    };
    try {
      fetch(this.endpoint, {
        method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: payload
      }).then(res => { if (res.status >= 500 || res.status === 429) retry(); }, retry);
    } catch { retry(); }
  }
}
